import { categoryMeta, NOTE_CATEGORIES } from '../copy';
import { latestUpdatedAt } from '../format';

import type {
  CoachingNoteCategory,
  CoachingNoteListCategory,
  NoteItem,
  NotesView,
} from '@/store/api/mcpMemberApi';

export type CategoryRef =
  | { category: CoachingNoteListCategory; kind: 'note' }
  | { kind: 'person'; personId: string };

// People share the contract's :category segment as `person-<person_id>` (contracts §8.14).
const PERSON_PREFIX = 'person-';

export const parseCategoryParam = (
  param: string | undefined,
): CategoryRef | null => {
  if (!param) return null;
  const category = NOTE_CATEGORIES.find((candidate) => candidate === param);
  if (category) return { category, kind: 'note' };
  if (param.startsWith(PERSON_PREFIX) && param.length > PERSON_PREFIX.length) {
    return { kind: 'person', personId: param.slice(PERSON_PREFIX.length) };
  }
  return null;
};

export interface NotesSection {
  category: CoachingNoteCategory;
  description: string;
  items: NoteItem[];
  name: string;
  personId?: string;
  updatedAt?: string;
}

const byPosition = (items: NoteItem[]): NoteItem[] =>
  [...items].sort((a, b) => a.position - b.position);

export const resolveSection = (
  view: NotesView,
  ref: CategoryRef,
): NotesSection | null => {
  if (ref.kind === 'note') {
    const meta = categoryMeta(ref.category, view.coach_first_name);
    return {
      category: ref.category,
      description: meta.description,
      items: byPosition(view.categories[ref.category] ?? []),
      name: meta.name,
      updatedAt: view.updated_at[ref.category],
    };
  }
  const person = view.people.find((candidate) => candidate.id === ref.personId);
  if (!person) return null;
  return {
    category: 'person',
    description: person.role,
    items: byPosition(person.items),
    name: person.name,
    personId: person.id,
    updatedAt: latestUpdatedAt(person.items),
  };
};
